/**
 * RPE_SCHEMA: field tables of every RPE chart object (W1-07a formats-json-rpe; contract formats/rpe/json.ts
 * `RpeSchema`). One table drives the lenient decoder (read.ts: integer types, required fields, nullability,
 * severities, the split between known keys and passthrough), the encoder (encode.ts `writeObject`: RPE key order
 * and the writer method of each scalar kind) and the inspector's "Phira ignores" / "RPE since" badges.
 *
 * Sources: rpe-format.md F§2-F§12 (Phira's serde structs: types, required = no serde default), the RPE-only keys
 * Phira ignores (F§2.1, F§5, F§6.1), and Phira's RPE docs for `rpeSince` (F§17: anchor/hitsound 142,
 * isGif/gifEvents 150, rotateWithFather 163, judgeArea/tint/tintHitEffects 170, timeTags 130, chartTime and
 * META.illustration 141).
 *
 * Order: each table lists its keys in RPE's write order, which is ASCII order (RPE serialises sorted maps;
 * uppercase before lowercase), except the chart table whose order is the exporter's: BPMList, META,
 * judgeLineGroup, judgeLineList, then the other RPE root keys.
 *
 * Kinds: 'json' marks structured members (lists, nested objects, the RPEVersion number-or-string) whose content is
 * described by other tables; 'triple' an `[i32, u32, u32]` beat; 'rgb' `[u8, u8, u8]`; 'bezier4' `[f32; 4]`.
 * Event `start` / `end` are 'json' because their type depends on the list (f32, rgb or string; see
 * `eventValueKind`).
 */

import type { Json } from '../../core/util/types';
import type { RpeFieldKind, RpeFieldSpec, RpeSchema } from './json';

/** RpeFieldSpec plus what the decoder needs beyond the contract. */
export interface RpeFieldSpecEx extends RpeFieldSpec {
  /** JSON null is accepted and means "absent" (serde Option). Otherwise null is a type error. */
  readonly nullable?: true;
  /**
   * Phira reads the field, but not in its chart parser: the META strings that only `fix_info_with` reads to infer
   * package metadata when there is no info.yml (F§2.2). A wrong type there skips that inference silently and the
   * chart still loads, so the decoder reports it as a 'note', never as 'phiraRejects'.
   */
  readonly readsOutsideParser?: true;
}

type Spec = RpeFieldSpecEx;

function f(
  key: string,
  kind: RpeFieldKind,
  required: 'phira' | 'no',
  phira: 'reads' | 'ignores',
  extra: {
    readonly default?: Json;
    readonly rpeSince?: number;
    readonly nullable?: true;
    readonly readsOutsideParser?: true;
  } = {},
): Spec {
  const spec: { -readonly [K in keyof Spec]: Spec[K] } = { key, kind, required, phira };
  if (extra.default !== undefined) spec.default = extra.default;
  if (extra.rpeSince !== undefined) spec.rpeSince = extra.rpeSince;
  if (extra.nullable !== undefined) spec.nullable = extra.nullable;
  if (extra.readsOutsideParser !== undefined) spec.readsOutsideParser = extra.readsOutsideParser;
  return Object.freeze(spec);
}

/** META strings read only by Phira's metadata inference (F§2.2), outside the chart parser. */
const INFO = { readsOutsideParser: true } as const;

const CHART: readonly Spec[] = [
  f('BPMList', 'json', 'phira', 'reads'),
  f('META', 'json', 'phira', 'reads'),
  f('judgeLineGroup', 'json', 'no', 'ignores', { default: ['Default'] }),
  f('judgeLineList', 'json', 'phira', 'reads'),
  f('chartTime', 'f64', 'no', 'ignores', { rpeSince: 141 }),
  f('multiLineString', 'string', 'no', 'ignores'),
  f('multiScale', 'f64', 'no', 'ignores'),
  f('timeTags', 'json', 'no', 'ignores', { rpeSince: 130 }),
  f('xybind', 'bool', 'no', 'ignores'),
];

const META: readonly Spec[] = [
  f('RPEVersion', 'json', 'no', 'reads', { default: 160 }),
  // Read only by Phira's package metadata inference (fix_info_with) when there is no info.yml (F§2.2): Phira's
  // chart parser itself only reads offset and RPEVersion, so a wrong type here never stops the chart loading.
  f('background', 'string', 'no', 'reads', INFO),
  f('charter', 'string', 'no', 'reads', INFO),
  f('composer', 'string', 'no', 'reads', INFO),
  f('id', 'string', 'no', 'ignores'),
  f('illustration', 'string', 'no', 'reads', { ...INFO, rpeSince: 141 }),
  f('level', 'string', 'no', 'reads', INFO),
  f('name', 'string', 'no', 'reads', INFO),
  f('offset', 'i32', 'phira', 'reads', { default: 0 }),
  f('song', 'string', 'no', 'reads', INFO),
];

const BPM: readonly Spec[] = [
  f('bpm', 'f64', 'phira', 'reads', { default: 120 }),
  f('startTime', 'triple', 'phira', 'reads', { default: [0, 0, 1] }),
];

const LINE: readonly Spec[] = [
  f('Group', 'i32', 'no', 'ignores', { default: 0 }),
  f('Name', 'string', 'phira', 'reads', { default: 'Untitled' }),
  f('Texture', 'string', 'phira', 'reads', { default: 'line.png' }),
  f('alphaControl', 'json', 'no', 'reads', { default: [] }),
  f('anchor', 'json', 'no', 'ignores', { default: [0.5, 0.5], rpeSince: 142 }),
  f('attachUI', 'string', 'no', 'reads', { nullable: true }),
  f('bpmfactor', 'f64', 'no', 'ignores', { default: 1 }),
  f('eventLayers', 'json', 'phira', 'reads', { default: [] }),
  f('extended', 'json', 'no', 'reads', { nullable: true }),
  f('father', 'i32', 'no', 'reads', { default: -1, nullable: true }),
  f('isCover', 'u8', 'phira', 'reads', { default: 1 }),
  f('isGif', 'bool', 'no', 'ignores', { default: false, rpeSince: 150 }),
  f('notes', 'json', 'no', 'reads', { default: [], nullable: true }),
  f('numOfNotes', 'i32', 'no', 'ignores', { default: 0 }),
  f('posControl', 'json', 'no', 'reads', { default: [] }),
  f('rotateWithFather', 'bool', 'no', 'reads', { default: false, rpeSince: 163, nullable: true }),
  f('sizeControl', 'json', 'no', 'reads', { default: [] }),
  f('skewControl', 'json', 'no', 'ignores'),
  f('yControl', 'json', 'no', 'reads', { default: [] }),
  f('zOrder', 'i32', 'no', 'reads', { default: 0 }),
];

const LAYER: readonly Spec[] = [
  f('alphaEvents', 'json', 'no', 'reads', { nullable: true }),
  f('moveXEvents', 'json', 'no', 'reads', { nullable: true }),
  f('moveYEvents', 'json', 'no', 'reads', { nullable: true }),
  f('rotateEvents', 'json', 'no', 'reads', { nullable: true }),
  f('speedEvents', 'json', 'no', 'reads', { nullable: true }),
];

const EXTENDED: readonly Spec[] = [
  f('colorEvents', 'json', 'no', 'reads', { nullable: true }),
  f('gifEvents', 'json', 'no', 'reads', { nullable: true, rpeSince: 150 }),
  f('inclineEvents', 'json', 'no', 'reads', { nullable: true }),
  f('paintEvents', 'json', 'no', 'reads', { nullable: true }),
  f('scaleXEvents', 'json', 'no', 'reads', { nullable: true }),
  f('scaleYEvents', 'json', 'no', 'reads', { nullable: true }),
  f('textEvents', 'json', 'no', 'reads', { nullable: true }),
];

const EVENT: readonly Spec[] = [
  f('bezier', 'u8', 'no', 'reads', { default: 0 }),
  f('bezierPoints', 'bezier4', 'no', 'reads', { default: [0, 0, 0, 0] }),
  f('easingLeft', 'f32', 'no', 'reads', { default: 0 }),
  f('easingRight', 'f32', 'no', 'reads', { default: 1 }),
  f('easingType', 'i32', 'no', 'reads', { default: 1 }),
  f('end', 'json', 'phira', 'reads'),
  f('endTime', 'triple', 'phira', 'reads', { default: [0, 0, 1] }),
  f('linkgroup', 'i32', 'no', 'ignores', { default: 0 }),
  f('start', 'json', 'phira', 'reads'),
  f('startTime', 'triple', 'phira', 'reads', { default: [0, 0, 1] }),
];

const NOTE: readonly Spec[] = [
  f('above', 'u8', 'phira', 'reads', { default: 1 }),
  f('alpha', 'u16', 'phira', 'reads', { default: 255 }),
  f('endTime', 'triple', 'phira', 'reads'),
  f('hitsound', 'string', 'no', 'reads', { nullable: true, rpeSince: 142 }),
  f('isFake', 'u8', 'phira', 'reads', { default: 0 }),
  f('judgeArea', 'f32', 'no', 'reads', { default: 1, nullable: true, rpeSince: 170 }),
  f('positionX', 'f32', 'phira', 'reads', { default: 0 }),
  f('size', 'f32', 'phira', 'reads', { default: 1 }),
  f('speed', 'f32', 'phira', 'reads', { default: 1 }),
  f('startTime', 'triple', 'phira', 'reads', { default: [0, 0, 1] }),
  f('tint', 'rgb', 'no', 'reads', { default: [255, 255, 255], nullable: true, rpeSince: 170 }),
  f('tintHitEffects', 'rgb', 'no', 'reads', { nullable: true, rpeSince: 170 }),
  f('type', 'u8', 'phira', 'reads', { default: 1 }),
  f('visibleTime', 'f64', 'phira', 'reads', { default: 999999 }),
  f('yOffset', 'f32', 'phira', 'reads', { default: 0 }),
];

/**
 * Control point keys. `x` and `easing` are required; exactly one value key is required per curve (pos for
 * posControl, alpha, size, y; skew for skewControl, which Phira ignores). Every other key must be numeric in Phira
 * (flattened into a HashMap<String, f32>).
 */
const CONTROL: readonly Spec[] = [
  f('alpha', 'f32', 'no', 'reads', { default: 1 }),
  f('easing', 'u8', 'phira', 'reads', { default: 1 }),
  f('pos', 'f32', 'no', 'reads', { default: 1 }),
  f('size', 'f32', 'no', 'reads', { default: 1 }),
  f('skew', 'f32', 'no', 'ignores', { default: 0 }),
  f('x', 'f64', 'phira', 'reads', { default: 0 }),
  f('y', 'f32', 'no', 'reads', { default: 1 }),
];

export const RPE_SCHEMA: RpeSchema = Object.freeze({
  chart: CHART,
  meta: META,
  bpm: BPM,
  line: LINE,
  layer: LAYER,
  extended: EXTENDED,
  event: EVENT,
  note: NOTE,
  control: CONTROL,
});

/** Object kinds of the schema. */
export type RpeObjectKind = keyof RpeSchema;

const BY_KEY = new Map<RpeObjectKind, ReadonlyMap<string, Spec>>(
  (Object.keys(RPE_SCHEMA) as RpeObjectKind[]).map((kind) => [
    kind,
    new Map((RPE_SCHEMA[kind] as readonly Spec[]).map((s) => [s.key, s] as const)),
  ]),
);

/** The field spec of `key` in an object kind (undefined for unknown = passthrough keys). */
export function fieldSpec(kind: RpeObjectKind, key: string): RpeFieldSpecEx | undefined {
  return BY_KEY.get(kind)?.get(key);
}

const KEYS = new Map<RpeObjectKind, readonly string[]>(
  (Object.keys(RPE_SCHEMA) as RpeObjectKind[]).map((kind) => [kind, Object.freeze(RPE_SCHEMA[kind].map((s) => s.key))]),
);

/** Known keys of an object kind, in RPE order (the order the encoder writes them in). */
export function knownKeys(kind: RpeObjectKind): readonly string[] {
  return KEYS.get(kind) ?? [];
}

/** True for keys the schema models (they never go to passthrough and a passthrough key never overrides them). */
export function isKnownKey(kind: RpeObjectKind, key: string): boolean {
  return BY_KEY.get(kind)?.has(key) ?? false;
}

/** True when Phira ignores this field (badge "Phira ignores", class rpeOnly). Unknown keys are ignored too. */
export function phiraIgnores(kind: RpeObjectKind, key: string): boolean {
  return fieldSpec(kind, key)?.phira !== 'reads';
}

/**
 * True when a malformed value of this field cannot make Phira refuse the chart: fields Phira ignores, unknown keys
 * and the META strings only its metadata inference reads (decoder severity 'note').
 */
export function phiraLoadsAnyValue(kind: RpeObjectKind, key: string): boolean {
  const spec = fieldSpec(kind, key);
  return spec === undefined || spec.phira === 'ignores' || spec.readsOutsideParser === true;
}

/** Event list keys of an eventLayers entry, in RPE order. */
export const LAYER_LIST_KEYS = ['alphaEvents', 'moveXEvents', 'moveYEvents', 'rotateEvents', 'speedEvents'] as const;
export type LayerListKey = (typeof LAYER_LIST_KEYS)[number];

/** Event list keys of `extended`, in RPE order. */
export const EXTENDED_LIST_KEYS = [
  'colorEvents',
  'gifEvents',
  'inclineEvents',
  'paintEvents',
  'scaleXEvents',
  'scaleYEvents',
  'textEvents',
] as const;
export type ExtendedListKey = (typeof EXTENDED_LIST_KEYS)[number];

/** Whether a string names an RPE event list (layer or extended). */
export function isEventListKey(key: string): key is LayerListKey | ExtendedListKey {
  return (
    (LAYER_LIST_KEYS as readonly string[]).includes(key) || (EXTENDED_LIST_KEYS as readonly string[]).includes(key)
  );
}

export type EventListKey = LayerListKey | ExtendedListKey;

/** Value type of the `start` / `end` of events in a list (F§6.1). */
export function eventValueKind(list: EventListKey): 'f32' | 'rgb' | 'string' {
  if (list === 'colorEvents') return 'rgb';
  if (list === 'textEvents') return 'string';
  return 'f32';
}

/** Control curve keys of a line and the value key each point must carry (F§12). */
export const CONTROL_CURVES = {
  alphaControl: 'alpha',
  posControl: 'pos',
  sizeControl: 'size',
  yControl: 'y',
} as const;
export type ControlCurveKey = keyof typeof CONTROL_CURVES;
export type ControlValueKey = (typeof CONTROL_CURVES)[ControlCurveKey];
export const CONTROL_CURVE_KEYS = Object.keys(CONTROL_CURVES) as readonly ControlCurveKey[];

/** RPE attachUI names in RPE's UI order (RPE shows them as numbers 1-7 in this order, F§10.5). */
export const UI_ELEMENT_NAMES = ['pause', 'combonumber', 'combo', 'score', 'bar', 'name', 'level'] as const;
